import type { FC } from 'react';

import { cn } from '@/utils/cn.util';
import type { StudentListItem } from '@/types/student.types';

interface UserActionsProps {
  user: StudentListItem;
  suspendingId: string | null;
  onView: (user: StudentListItem) => void;
  onSuspend: (user: StudentListItem) => void;
  onActivate: (user: StudentListItem) => void;
}

const btnBase =
  'rounded-md border px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50';
const btnNeutral =
  'border-[var(--color-divider)] bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)] hover:border-brand-500 hover:bg-brand-50 hover:text-brand-600';
const btnDanger =
  'border-[var(--color-divider)] bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)] hover:border-[var(--color-danger)] hover:bg-[var(--color-danger-bg)] hover:text-[var(--color-danger-ink)]';

const UserActions: FC<UserActionsProps> = ({
  user,
  suspendingId,
  onView,
  onSuspend,
  onActivate,
}) => {
  const isSuspending = suspendingId === user.id;
  // Deletion is terminal: the server rejects any status change on these, so offering one here
  // would only produce a 409. View still works — the drawer is where the deletion record lives.
  const isDeleted = user.accountStatus === 'deleted';

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button type="button" className={cn(btnBase, btnNeutral)} onClick={() => onView(user)}>
        View
      </button>

      {isDeleted ? null : user.accountStatus === 'active' ? (
        <button
          type="button"
          className={cn(btnBase, btnDanger)}
          onClick={() => onSuspend(user)}
          disabled={isSuspending}
        >
          {isSuspending ? 'Suspending…' : 'Suspend'}
        </button>
      ) : (
        <button
          type="button"
          className={cn(btnBase, btnNeutral)}
          onClick={() => onActivate(user)}
          disabled={isSuspending}
        >
          {isSuspending ? 'Activating…' : 'Activate'}
        </button>
      )}
    </div>
  );
};

export default UserActions;
